import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

import ottMain from '../Images/ott.jpg';
import heroImage from '../Images/ottpage.jpg';
import Footer from './Footer';

const imageDetails = {
  [ottMain]: {
    category: 'General OT Tables',
    title: 'Model: (MM OTX 500)',
    description:
      'MM OTX 500 is a high-performance electro-mechanical operation theatre table designed to provide smooth positioning, advanced surgical flexibility, and superior patient support for modern operating rooms. With noiseless motorized movements, radiolucent tabletop compatibility, ergonomic controls, and durable stainless steel construction, it ensures precision, reliability, and efficient surgical workflow.',
    sections: {
      'Advanced Electro-Mechanical Operation': {
        icon: '⚙️',
        items: [
          'Electric-manual drives for smooth and jerk-free positioning',
          'Noiseless motorized movement system',
          'Reliable and stable operation during surgical procedures',
          'Designed for advanced operating theater environments',
          'Dual control operation for enhanced convenience',
        ],
      },

      'Intelligent Positioning Control': {
        icon: '🎛️',
        items: [
          'Height adjustment support',
          'Trendelenburg & Reverse Trendelenburg positioning',
          'Lateral tilt adjustment',
          'Back section & chair positioning',
          'Flex and reflex positioning support',
          'Controlled through corded handset and manual override system',
          'Graphical/icon-based operation interface',
        ],
      },

      'Safety & Stability': {
        icon: '🔒',
        items: [
          'Safety key prevents accidental motor operation during critical procedures',
          'Stable and secure table movement',
          'Smooth transition between positions',
          'Designed for patient and operator safety',
          'Reliable support during complex surgeries',
        ],
      },

      'Multi-Section Tabletop Design': {
        icon: '🛏️',
        items: [
          'Tabletop divided into multiple sections for flexible articulation',
          'Supports numerous surgical positioning possibilities',
          'Ergonomic design for improved patient comfort',
          'Suitable for multiple surgical specialties',
        ],
      },

      'C-ARM & X-Ray Compatibility': {
        icon: '🩻',
        items: [
          'Fully radiolucent tabletop',
          'C-ARM and X-ray compatible across the entire table length',
          'No crossbar design for unobstructed fluoroscopy access',
          'Offset column design allows unrestricted C-ARM movement',
          'Ideal for imaging-guided surgical procedures',
        ],
      },

      'Smart Functional Features': {
        icon: '💡',
        items: [
          'Zero position function restores neutral table position instantly',
          'Integrated weight compensation through manual adjustment',
          'Easy positioning control for head and leg sections',
          'Efficient workflow support for surgical teams',
        ],
      },

      'Durable Stainless Steel Construction': {
        icon: '🏥',
        items: [
          'Non-corrosive SS 304 stainless steel construction',
          'Stainless steel hydraulic/electric control accessory rails',
          'Strong and durable base ensures long-term clinical service',
          'Easy cleaning and hygienic surface finish',
          'Easy to clean and maintain',
        ],
      },
    },
  },
};

const OTTdetail = () => {
  const [isMobile, setIsMobile] = useState(false);
  const introRef = useRef(null);

  useLayoutEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const detail = imageDetails[ottMain];
  const sections = detail?.sections || {};

  return (
    <>
      <Helmet>
        <title>Operation Theatre Table | Mindron Meditech</title>
        <meta
          name="description"
          content="Explore MM OTX 500 operation theatre table by Mindron Meditech with electro-mechanical movement, C-ARM compatibility, radiolucent tabletop, stainless steel construction, and advanced surgical positioning."
        />
        <link
          rel="canonical"
          href="https://www.mindronmeditech.com/products/ott"
        />
      </Helmet>

      <div
        style={{
          fontFamily: 'sans-serif',
          margin: 0,
          padding: 0,
          overflowX: 'hidden',
        }}
      >
        {/* ── HERO ── */}
        <div
          style={{
            height: isMobile ? '30vh' : '80vh',
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <img
            src={heroImage}
            alt="Hero"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'fill',
              display: 'block',
            }}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,20,60,0.5) 0%, transparent 60%)',
            }}
          />
        </div>

        {/* ── PRODUCT INTRO ── */}
        <div
          ref={introRef}
          style={{
            display: 'flex',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: '40px',
            padding: isMobile ? '32px 6%' : '48px 6%',
            alignItems: 'flex-start',
          }}
        >
          {/* Left: text */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div
              style={{
                display: 'inline-block',
                backgroundColor: '#e8f0fe',
                color: '#1a56db',
                fontSize: '13px',
                fontWeight: '600',
                padding: '4px 14px',
                borderRadius: '20px',
                marginBottom: '14px',
              }}
            >
              {detail?.category}
            </div>

            <h1
              style={{
                fontSize: isMobile ? '26px' : '38px',
                fontWeight: '800',
                color: '#0a1628',
                margin: '0 0 8px',
                lineHeight: 1.2,
              }}
            >
              {detail?.title}
            </h1>

            <h2
              style={{
                fontSize: isMobile ? '16px' : '20px',
                fontWeight: '500',
                color: '#444',
                margin: '0 0 20px',
              }}
            >
              {detail?.heading}
            </h2>

            <p
              style={{
                fontSize: '15px',
                color: '#555',
                lineHeight: '1.8',
                maxWidth: '520px',
              }}
            >
              {detail?.description}
            </p>
          </div>

          {/* Right: product image */}
          <div
            style={{
              flex: '0 0 auto',
              width: isMobile ? '100%' : '340px',
              background: '#f4f6fb',
              borderRadius: '14px',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={ottMain}
              alt="OT Table"
              style={{
                width: '100%',
                maxHeight: '260px',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </div>
        </div>

        {/* ── KEY FEATURES GRID ── */}
        <div
          style={{
            padding: isMobile ? '16px 6% 40px' : '16px 6% 56px',
          }}
        >
          <div
            style={{
              width: '50px',
              height: '5px',
              backgroundColor: '#cc0000',
              borderRadius: '2px',
              marginBottom: '12px',
            }}
          />

          <h2
            style={{
              fontSize: '22px',
              fontWeight: '800',
              color: '#0a1628',
              marginBottom: '28px',
            }}
          >
            Key Features
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px',
            }}
          >
            {Object.entries(sections).map(([sectionName, sectionData], idx) => (
              <div
                key={idx}
                style={{
                  background: '#f4f6fb',
                  borderRadius: '12px',
                  padding: '22px 20px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '14px',
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{sectionData.icon}</span>

                  <h3
                    style={{
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#0a1628',
                      margin: 0,
                    }}
                  >
                    {sectionName}
                  </h3>
                </div>

                <ul
                  style={{
                    margin: 0,
                    paddingLeft: '18px',
                    listStyle: 'disc',
                  }}
                >
                  {sectionData.items.map((item, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: '13px',
                        color: '#555',
                        lineHeight: '1.8',
                      }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <Footer />

        <style>{`
          *::-webkit-scrollbar {
            display: none;
          }

          * {
            scrollbar-width: none;
          }

          body {
            overflow-x: hidden;
          }
        `}</style>
      </div>
    </>
  );
};

export default OTTdetail;